/*
 * PROD-7828 R5 — pure adapters: goserver Overview payloads (the contract of record) -> view models.
 * The JSX renders only what these return. adapters.test.js runs them against the backend-generated
 * __fixtures__ so a wire-shape drift fails here, not on screen.
 */
import { fmt, fmt_signed, month_label, num } from '../format';
import { rule_ref_of } from '../rules_engine/logic';
import { session_status_label, status_bucket } from './drilldown_logic';
import { DRILLDOWN_CATEGORY_UNATTRIBUTED, DRILLDOWN_KIND_ADDITIONAL } from './params';
import {
  ENTRY_TYPE_LABELS,
  is_system_row,
  source_text,
  who_view
} from '../additional_payments/adapters';

export const SHADOW_BANNER_TITLE = 'Shadow run — not final.';
export const SHADOW_BANNER_COPY =
  'These amounts are being validated against the manual sheet and are not the payroll source of truth yet.';

const is_present = (v) => v !== null && v !== undefined && v !== '';

// F7: the payload keys the renderers read. adapters.test.js pins them to overview_enums.json (goserver's contract
// lists, asserted on every handler response) and to every fixture row, so a renamed JSON tag fails a suite.
export const L1_AMOUNT_KEYS = [
  'one_on_one_amount',
  'group_class_amount',
  'trial_amount',
  'incentive_amount',
  'additional_amount',
  'penalty_amount',
  'waived_penalty_amount',
  'rounded_net_payable'
];
export const L1_ROW_KEYS = [
  'teacher_uuid',
  'teacher_name',
  'teacher_email',
  'teacher_slug',
  'lifecycle',
  'reopen_count',
  'needs_review',
  'needs_recalc',
  'compute_error'
];
export const L2_META_KEYS = [
  'teacher_uuid',
  'teacher_name',
  'teacher_email',
  'teacher_slug',
  'base_rate_snapshot',
  'is_gst_registered',
  'free_pass_balance',
  'needs_review',
  'needs_recalc',
  'compute_error',
  'rate_changes_in_month'
];
export const FINAL_PAYABLE_KEYS = ['key', 'label', 'amount', 'sign', 'is_informational', 'explain'];
export const MONTH_SUMMARY_KEYS = [
  'needs_review_count',
  'active_penalty_count',
  'pending_review_count',
  'blocking_open_count'
];
// R6 (CD-205/CD-206): the penalty-money breakdown keys, pinned to overview_enums.json like the lists above.
export const L2_SUMMARY_ROW_KEYS = [
  'category',
  'label',
  'sessions_count',
  'amount',
  'penalty_amount',
  'waived_amount',
  'correction_amount',
  'free_pass_amount',
  'uncharged_orphan_amount',
  'net_amount'
];
export const L2_UNATTRIBUTED_KEYS = [
  'unattributed_penalty_amount',
  'unattributed_waived_amount',
  'unattributed_correction_amount',
  'unattributed_free_pass_amount',
  'unattributed_uncharged_orphan_amount'
];
export const DRILLDOWN_PENALTY_KEYS = ['amount', 'original_amount', 'corrected_amount', 'money'];
export const PENALTY_MONEY_KEYS = ['charged', 'waived', 'pass', 'correction', 'uncharged_orphan'];

export const NEEDS_REVIEW_TEXT = '⚠ needs review';

// ---- /months ----

// Month badge from the per-lifecycle teacher counts: all CLOSED -> Closed, any OPEN -> Open, else Review.
export const month_badge = (lifecycles) => {
  const lc = lifecycles && typeof lifecycles === 'object' ? lifecycles : {};
  const total = Object.keys(lc).reduce((acc, k) => acc + num(lc[k]), 0);
  if (total === 0) return { key: '', label: '' };
  if (num(lc.CLOSED) === total) return { key: 'CLOSED', label: 'Closed' };
  if (num(lc.OPEN) > 0) return { key: 'OPEN', label: 'Open' };
  return { key: 'REVIEW', label: 'Review' };
};

export const months_view = (data) => {
  const list = data && Array.isArray(data.months) ? data.months : [];
  return {
    workers_enabled: !!(data && data.workers_enabled),
    months: list.map((m) => ({
      pay_month: m.pay_month,
      label: month_label(m.pay_month),
      teacher_count: num(m.teacher_count),
      badge: month_badge(m.lifecycles)
    }))
  };
};

// ---- /month_summary -> tab badges (keyed by the shell's tab keys) ----

// D73: Needs attention badges the OPEN issues that block payroll (registry tier ERRORS / MUST_REVIEW).
// A backend older than D73 omits blocking_open_count; all open issues (pending_review_count) is a
// superset of it, so the fallback can over-count but never hides blocking work behind a false zero.
export const tab_badges = (summary) => {
  const s = summary || {};
  return {
    overview: num(s.needs_review_count),
    penalties: num(s.active_penalty_count),
    'needs-attention': num(
      is_present(s.blocking_open_count) ? s.blocking_open_count : s.pending_review_count
    )
  };
};

// ---- /l1 ----

// Tone is presentation only: penalties read negative, money-in cards positive, total is the brand card.
const STAT_TONES = {
  total_payment: 'brand',
  incentives: 'pos',
  waivers: 'pos',
  additional: 'pos',
  penalties: 'neg'
};

// F2: totals stay complete (stale rows included at their last values); the payout card says how many are stale.
export const review_caveat = (needs_review_count) => {
  const n = num(needs_review_count);
  if (n <= 0) return '';
  return `includes ${n} ${n === 1 ? 'teacher' : 'teachers'} needing review`;
};

export const build_stat_cards = (cards, needs_review_count) => {
  const caveat = review_caveat(needs_review_count);
  return (Array.isArray(cards) ? cards : []).map((c, i) => ({
    key: c.key || `card-${i}`,
    label: c.label || c.key || `Card ${i + 1}`,
    value: fmt(c.amount),
    tone: STAT_TONES[c.key] || '',
    chip: c.key === 'total_payment' ? caveat : ''
  }));
};

// Full uuid stays on the row for the title/hover; the cell shows the first segment.
export const short_uuid = (uuid) => {
  if (!uuid) return '';
  const s = String(uuid);
  return s.length > 13 ? `${s.slice(0, 8)}…` : s;
};

export const l1_view = (data) => {
  const d = data || {};
  return {
    shadow_banner: d.shadow_banner === true,
    stat_cards: build_stat_cards(d.stat_cards, d.needs_review_count),
    rows: Array.isArray(d.rows) ? d.rows : [],
    total_count: num(d.total_count),
    needs_review_count: num(d.needs_review_count)
  };
};

// ---- /l2 ----

// R6 (CD-205): true when there is anything to drill into on the penalties column — a pass or an
// uncharged orphan can be the only nonzero figure on a category (D194: a pass isn't a waiver).
const has_penalty_activity = (penalty, waived, correction, free_pass, uncharged_orphan) =>
  penalty !== 0 || waived !== 0 || correction !== 0 || free_pass !== 0 || uncharged_orphan !== 0;

export const summary_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.summary_rows) ? d.summary_rows : []).map((r) => {
    const penalty_amount = num(r.penalty_amount);
    const waived_amount = num(r.waived_amount);
    const correction_amount = num(r.correction_amount);
    const free_pass_amount = num(r.free_pass_amount);
    const uncharged_orphan_amount = num(r.uncharged_orphan_amount);
    return {
      category: r.category,
      label: r.label || r.category,
      sessions_count: num(r.sessions_count),
      amount: num(r.amount),
      // Additional nets negative when a carry-forward outweighs the month's entries.
      amount_tone: num(r.amount) < 0 ? 'tp-neg' : 'tp-pos',
      penalty_amount,
      waived_amount,
      correction_amount,
      free_pass_amount,
      uncharged_orphan_amount,
      net_amount: num(r.net_amount),
      can_drill_items: num(r.sessions_count) > 0 || num(r.amount) !== 0,
      can_drill_penalties: has_penalty_activity(
        penalty_amount,
        waived_amount,
        correction_amount,
        free_pass_amount,
        uncharged_orphan_amount
      )
    };
  });
  const penalty = num(d.unattributed_penalty_amount);
  const waived = num(d.unattributed_waived_amount);
  const correction = num(d.unattributed_correction_amount);
  const free_pass = num(d.unattributed_free_pass_amount);
  const uncharged_orphan = num(d.unattributed_uncharged_orphan_amount);
  // Penalties not linked to a session type (manual, month-level, legacy NULL category): own line, drillable, never dropped.
  const unattributed = has_penalty_activity(
    penalty,
    waived,
    correction,
    free_pass,
    uncharged_orphan
  )
    ? {
        category: DRILLDOWN_CATEGORY_UNATTRIBUTED,
        penalty_amount: penalty,
        waived_amount: waived,
        correction_amount: correction,
        free_pass_amount: free_pass,
        uncharged_orphan_amount: uncharged_orphan,
        // The line exists only with penalty activity, so it always drills.
        can_drill_penalties: true
      }
    : null;
  return { rows, unattributed };
};

const SIGN_TONES = { '+': 'tp-pos', '-': 'tp-neg', '=': 'tp-strong' };

// Backend order is the arithmetic order; informational lines (carry forward, rounded) are shown, not summed.
const WHOLE_RUPEE_LINES = ['rounded_net_payable', 'payable'];

const payable_amount_text = (l, stale) => {
  if (stale) return NEEDS_REVIEW_TEXT;
  return WHOLE_RUPEE_LINES.indexOf(l.key) !== -1 ? fmt(l.amount) : fmt(l.amount, 2);
};

// B1: 'payable' (max(rounded net, 0)) is the payout total. F1: a stale/errored month shows "needs review" on every
// line and no popover — a final-looking number there is exactly what D-11 forbids.
export const final_payable_view = (lines, stale = false) =>
  (Array.isArray(lines) ? lines : []).map((l) => ({
    key: l.key,
    label: l.label || l.key,
    sign: l.sign || '',
    amount_text: payable_amount_text(l, stale),
    tone: stale ? 'tp-neg' : SIGN_TONES[l.sign] || '',
    is_informational: l.is_informational === true,
    is_total: l.key === 'payable',
    // D-8: persisted monthly_explain line, verbatim; null for months computed before r5 (no popover).
    explain: stale || l.explain === undefined ? null : l.explain
  }));

const net_of = (totals) =>
  totals &&
  typeof totals === 'object' &&
  is_present(totals.net) &&
  Number.isFinite(Number(totals.net))
    ? Number(totals.net)
    : null;

// The persisted recalc audit is {totals, counts, notes} (after-state only); a before/after pair is
// shown only when the diff actually carries it.
// before = audit old_value {totals}; diff = audit new_value {totals, ...}.
export const history_net = (diff, before_value) => {
  if (!diff || typeof diff !== 'object') return { net_before: null, net_after: null, net_text: '' };
  const before =
    before_value && typeof before_value === 'object'
      ? net_of(before_value.totals)
      : net_of(diff.totals_before);
  const after =
    net_of(diff.totals_after) !== null ? net_of(diff.totals_after) : net_of(diff.totals);
  let net_text = '';
  if (before !== null && after !== null) net_text = `${fmt(before, 2)} → ${fmt(after, 2)}`;
  else if (after !== null) net_text = fmt(after, 2);
  return { net_before: before, net_after: after, net_text };
};

export const history_view = (rows) => {
  const list = (Array.isArray(rows) ? rows : []).map((h, idx) => ({
    key: `${h.created_at || 'na'}-${idx}`,
    when: h.created_at || '—',
    action: h.action || '—',
    actor: h.actor || '—',
    reason: h.reason || '—',
    ...history_net(h.diff, h.before)
  }));
  return { rows: list, show_net: list.some((r) => r.net_text !== '') };
};

export const incentives_view = (rows) =>
  (Array.isArray(rows) ? rows : []).map((i, idx) => ({
    key: `${i.item_type}-${i.category}-${i.session_uuid || idx}`,
    category: i.category || '—',
    item_type: i.item_type || '—',
    amount_text: fmt_signed(i.amount, 2),
    session_uuid: i.session_uuid || '',
    explain: i.explain === undefined ? null : i.explain
  }));

/*
 * One additional-payment entry (goserver additional.Entry) as L2 and the ADDITIONAL drill-down show it.
 * Signed: Ops entries add, the SYSTEM carry-forward is the only negative row and must read as one.
 */
export const additional_entry_view = (r, idx) => {
  const amount = num(r.amount);
  const added = who_view(r.added_by_name, r.added_by);
  return {
    key: `additional-${r.uuid || idx}`,
    kind: DRILLDOWN_KIND_ADDITIONAL,
    type_label: ENTRY_TYPE_LABELS[r.entry_type] || r.entry_type || '—',
    date_text: r.entry_at_ist || '—',
    category_label: r.category_label || r.category || '—',
    reference_id: r.reference_id || '',
    source_text: source_text(r) || '—',
    is_carry_forward: is_system_row(r),
    session_uuid: r.related_session_uuid || '',
    session_short: short_uuid(r.related_session_uuid),
    amount,
    amount_text: fmt_signed(amount, 2),
    amount_tone: amount < 0 ? 'tp-neg' : 'tp-pos',
    note: r.note || '',
    added_by: added.text,
    added_by_title: added.title,
    explain: r.explain === undefined ? null : r.explain
  };
};

// Header totals come from the final_payable lines, so the header and the Final payable tab can't disagree.
const line_amount = (lines, key) => {
  const l = (Array.isArray(lines) ? lines : []).find((x) => x && x.key === key);
  return l ? l.amount : null;
};

export const l2_view = (data) => {
  const d = data || {};
  const meta = d.teacher_meta || {};
  const month_row = d.month_row || {};
  return {
    meta,
    // teacher_meta has no amounts; the rounded net lives on month_row (D-3).
    rounded_net_payable: month_row.rounded_net_payable,
    gross: line_amount(d.final_payable, 'gross'),
    gst: line_amount(d.final_payable, 'gst'),
    tds: line_amount(d.final_payable, 'tds'),
    needs_review: meta.needs_review === true,
    shadow_banner: d.shadow_banner === true,
    summary: summary_view(d),
    incentives: incentives_view(d.incentives),
    additional: (Array.isArray(d.additional) ? d.additional : []).map(additional_entry_view),
    final_payable: final_payable_view(d.final_payable, meta.needs_review === true),
    history: history_view(d.computation_history)
  };
};

// ---- /drilldown ----

const INCENTIVE_ITEM_TYPES = ['INCENTIVE', 'ADDL_PARTICIPANT', 'ENCASHMENT'];

const item_type_label = (r) => {
  const flags = [];
  if (r.is_surge) flags.push('surge');
  if (r.is_employee_session) flags.push('employee');
  if (num(r.multiplier) && num(r.multiplier) !== 1) flags.push(`×${num(r.multiplier)}`);
  const base = r.item_type === 'SESSION_PAYMENT' ? r.category : `${r.item_type} · ${r.category}`;
  return flags.length ? `${base} (${flags.join(', ')})` : base;
};

const penalty_type_label = (r) => {
  const pct = num(r.penalty_pct) ? ` ${num(r.penalty_pct)}%` : '';
  const parts = [`${r.penalty_type || 'PENALTY'}${pct}`, r.waiver_status, r.source].filter(Boolean);
  if (r.is_overridden) parts.push('overridden');
  if (r.is_orphaned) parts.push('orphaned');
  return parts.join(' · ');
};

// Every row (item or penalty) carries its own persisted session_status; a session with none reads Unknown.
// An ADDITIONAL drill-down row is an entry, not a session item (Q23), and has its own view.
export const drilldown_row_view = (r, idx) => {
  if (r.kind === DRILLDOWN_KIND_ADDITIONAL) return additional_entry_view(r, idx);
  const is_penalty = r.kind === 'penalty';
  const is_incentive = !is_penalty && INCENTIVE_ITEM_TYPES.indexOf(r.item_type) !== -1;
  const amount = num(r.amount);
  const bucket = status_bucket(r);
  // R6: money is the persisted per-row breakdown (paycalc reconcile.Money); only penalty rows carry it.
  const money = is_penalty && r.money ? r.money : null;
  const has_correction =
    is_penalty && r.corrected_amount !== null && r.corrected_amount !== undefined;
  return {
    key: `${r.kind}-${r.session_uuid || 'month'}-${r.penalty_type || r.item_type}-${idx}`,
    kind: is_penalty ? 'penalty' : 'item',
    session_uuid: r.session_uuid || '',
    session_short: short_uuid(r.session_uuid),
    time_label: r.start_time_ist || '—',
    session_status: bucket,
    status_label: bucket ? session_status_label(bucket) : '—',
    duration_label: r.duration_mins ? `${r.duration_mins} mins` : '—',
    type_label: is_penalty ? penalty_type_label(r) : item_type_label(r),
    payment: !is_penalty && !is_incentive ? amount : null,
    incentive: is_incentive ? amount : null,
    penalty: is_penalty ? amount : null,
    is_orphaned: is_penalty && r.is_orphaned === true,
    original_amount: is_penalty ? num(r.original_amount) : null,
    corrected_amount: has_correction ? num(r.corrected_amount) : null,
    waived_amount: is_penalty ? num(money && money.waived) : null,
    free_pass_amount: is_penalty ? num(money && money.pass) : null,
    uncharged_orphan_amount: is_penalty ? num(money && money.uncharged_orphan) : null,
    note: r.note || '',
    rule_ref: rule_ref_of(r),
    explain: r.explain === undefined ? null : r.explain
  };
};

export const drilldown_view = (data) => {
  const d = data || {};
  const days = Array.isArray(d.days) ? d.days : [];
  let idx = 0;
  const view_days = days.map((day) => ({
    key: day.is_month_level ? 'month-level' : day.date,
    label: day.is_month_level ? 'Month-level (no session time)' : day.date,
    rows: (Array.isArray(day.rows) ? day.rows : []).map((r) => {
      idx += 1;
      return drilldown_row_view(r, idx);
    })
  }));
  return {
    days: view_days,
    show_payment_col: d.show_payment_col !== false,
    count: idx
  };
};
